// Configuração da API
const BASE_URL = import.meta.env.VITE_API_URL || 'https://api.agroov.com'
const API_URL = BASE_URL.endsWith('/api') ? BASE_URL : `${BASE_URL}/api`

// Função auxiliar para formatar URLs de fotos
export const getFotoUrl = (url) => {
    if (!url) return '';
    
    let processedUrl = url;
    
    // Forçar HTTPS em qualquer URL absoluta (evita problemas de mixed content e erro de carregamento)
    if (processedUrl.startsWith('http://')) {
        processedUrl = processedUrl.replace('http://', 'https://');
    }

    // Se for um vídeo do Cloudinary, trocar a extensão para .jpg para gerar um thumbnail automático
    if (processedUrl.includes('/video/upload/') || processedUrl.match(/\.(mp4|mov|avi|webm)$/i)) {
        if (processedUrl.match(/\.(mp4|mov|avi|webm)$/i)) {
            processedUrl = processedUrl.replace(/\.(mp4|mov|avi|webm)$/i, '.jpg');
        } else {
            processedUrl += '.jpg';
        }
        // Forçar a conversão de vídeo para imagem na URL se necessário
        processedUrl = processedUrl.replace('/video/upload/', '/video/upload/w_400,c_fill,q_auto,f_jpg/');
    }

    // Se for uma imagem do Cloudinary, aplicar otimizações automáticas para o navegador (f_auto, q_auto)
    if (processedUrl.includes('/image/upload/')) {
        if (!processedUrl.includes('/w_')) {
            // No site principal, podemos usar f_auto (formato automático como webp/avif) para máxima performance
            processedUrl = processedUrl.replace('/image/upload/', '/image/upload/w_800,c_limit,q_auto,f_auto/');
        }
    }

    if (processedUrl.startsWith('http')) return processedUrl;
    
    // Remove barras extras e garante o endereço da API
    const cleanUrl = processedUrl.startsWith('/') ? processedUrl : `/${processedUrl}`;
    const cleanBase = BASE_URL.replace(/\/$/, '');
    
    // Forçar https://api.agroov.com em produção se necessário
    const finalBase = cleanBase.includes('localhost') ? cleanBase : 'https://api.agroov.com';
    
    return `${finalBase}${cleanUrl}`;
}

// Função auxiliar para fazer requisições
async function request(endpoint, options = {}) {
    const token = localStorage.getItem('token')

    const headers = {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers
    }

    // Apenas adicionar Content-Type se NÃO for FormData (o browser define automaticamente)
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json'
    }

    const config = {
        headers,
        ...options
    }

    try {
        const response = await fetch(`${API_URL}${endpoint}`, config)
        const data = await response.json()

        if (!response.ok) {
            // Se for erro de autenticação, força o logout automático
            if (response.status === 401) {
                localStorage.removeItem('token')
                localStorage.removeItem('usuario')
                // Apenas redireciona se não estiver na página de login ou cadastro
                if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/cadastro')) {
                    window.location.href = '/login?sessao_expirada=true'
                }
            }
            throw new Error(data.message || 'Erro na requisição')
        }

        return data
    } catch (error) {
        console.error('Erro na API:', error)
        throw error
    }
}

// Serviços de Autenticação
export const authService = {
    async cadastro(dados) {
        const response = await request('/auth/cadastro', {
            method: 'POST',
            body: JSON.stringify(dados)
        })
        if (response.token) {
            localStorage.setItem('token', response.token)
            localStorage.setItem('usuario', JSON.stringify(response.usuario))
        }
        return response
    },

    async login(email, senha) {
        const response = await request('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, senha })
        })
        if (response.token) {
            localStorage.setItem('token', response.token)
            localStorage.setItem('usuario', JSON.stringify(response.usuario))
        }
        return response
    },

    async loginGoogle(credential) {
        const response = await request('/auth/google', {
            method: 'POST',
            body: JSON.stringify({ credential })
        })
        if (response.token) {
            localStorage.setItem('token', response.token)
            localStorage.setItem('usuario', JSON.stringify(response.usuario))
        }
        return response
    },

    async getUsuarioLogado() {
        return request('/auth/me')
    },

    logout() {
        localStorage.removeItem('token')
        localStorage.removeItem('usuario')
        window.location.href = '/login'
    },

    isLogado() {
        return !!localStorage.getItem('token')
    },

    getUsuario() {
        const usuario = localStorage.getItem('usuario')
        return usuario ? JSON.parse(usuario) : null
    },

    async completarPerfil(dados) {
        const response = await request('/auth/completar-perfil', {
            method: 'POST',
            body: JSON.stringify(dados)
        })
        if (response.token) {
            localStorage.setItem('token', response.token)
            localStorage.setItem('usuario', JSON.stringify(response.usuario))
        }
        return response
    },

    async trocarTipo(novoTipo) {
        const response = await request('/auth/trocar-tipo', {
            method: 'POST',
            body: JSON.stringify({ novoTipo })
        })
        if (response.token) {
            localStorage.setItem('token', response.token)
            localStorage.setItem('usuario', JSON.stringify(response.usuario))
        }
        return response
    },

    async adicionarTipo(dados) {
        const response = await request('/auth/adicionar-tipo', {
            method: 'POST',
            body: JSON.stringify(dados)
        })
        if (response.token) {
            localStorage.setItem('token', response.token)
            localStorage.setItem('usuario', JSON.stringify(response.usuario))
        }
        return response
    }
}

// Serviços de Anúncios
export const anunciosService = {
    async listar(filtros = {}) {
        const params = new URLSearchParams()
        Object.entries(filtros).forEach(([key, value]) => {
            if (value) params.append(key, value)
        })
        const query = params.toString() ? `?${params.toString()}` : ''
        return request(`/anuncios${query}`)
    },

    async buscarPorId(id) {
        return request(`/anuncios/${id}`)
    },

    async criar(dados) {
        return request('/anuncios', {
            method: 'POST',
            body: JSON.stringify(dados)
        })
    },

    async atualizar(id, dados) {
        return request(`/anuncios/${id}`, {
            method: 'PUT',
            body: JSON.stringify(dados)
        })
    },

    async deletar(id) {
        return request(`/anuncios/${id}`, {
            method: 'DELETE'
        })
    },

    async marcarVendido(id) {
        return request(`/anuncios/${id}/vendido`, {
            method: 'POST'
        })
    }
}

// Serviços de Usuários
export const usuariosService = {
    async buscarPerfil(id) {
        return request(`/usuarios/perfil/${id}`)
    },

    async atualizarPerfil(dados) {
        return request('/usuarios/perfil', {
            method: 'PUT',
            body: JSON.stringify(dados)
        })
    },

    async listarMotoristas() {
        return request('/usuarios/motoristas')
    }
}

// Serviços de Upload
export const uploadService = {
    async uploadImagem(file) {
        const formData = new FormData()
        formData.append('image', file)

        return request('/upload', {
            method: 'POST',
            body: formData
        })
    }
}

// Serviços de Fretes
export const fretesService = {
    async candidatar(dados) {
        return request('/fretes/candidatar', {
            method: 'POST',
            body: JSON.stringify(dados)
        })
    },

    async minhasCandidaturas() {
        return request('/fretes/minhas-candidaturas')
    },

    async candidaturasPorAnuncio(anuncioId) {
        return request(`/fretes/candidaturas/${anuncioId}`)
    },

    async aceitar(id, resposta) {
        return request(`/fretes/aceitar/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ resposta })
        })
    },

    async rejeitar(id, resposta) {
        return request(`/fretes/rejeitar/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ resposta })
        })
    },

    async proximos() {
        return request('/fretes/proximos')
    },

    async contadorProximos() {
        return request('/fretes/contador-proximos')
    }
}

// Serviços de Chat
export const chatService = {
    async listarConversas() {
        return request('/chat/conversas')
    },

    async listarMensagens(usuarioId) {
        return request(`/chat/${usuarioId}`)
    },

    async enviarMensagem(dados) {
        return request('/chat', {
            method: 'POST',
            body: JSON.stringify(dados)
        })
    }
}

// Serviços de Favoritos
export const favoritosService = {
    async toggle(anuncioId) {
        return request('/favoritos', {
            method: 'POST',
            body: JSON.stringify({ anuncioId })
        })
    },

    async listar() {
        return request('/favoritos')
    },

    async checarStatus(anuncioId) {
        return request(`/favoritos/${anuncioId}`)
    }
}

export default { authService, anunciosService, usuariosService, fretesService, uploadService, chatService, favoritosService }
